/**
 * useIntegrationStatus — FM7 Integration Capability State Hook
 * Authorization: FM7_IMPLEMENTATION_PLAN.md (BOUND-REOPEN-FM7-01)
 *
 * FM7 STATUS TRUTH (INV-FM7-FOUND-01):
 *   isActive(category) returns true ONLY when is_active=true for that category.
 *   CONFIGURED ≠ ACTIVE — test must pass AND admin must enable.
 *
 * Uses the public /finance/integrations/capability endpoint which:
 *   - Requires only authentication (no finance.integrations.view permission)
 *   - Returns only category → is_active boolean map (no sensitive config)
 *   - Accessible to students, finance_officers, and all other roles
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

export function useIntegrationStatus() {
  const [capabilities, setCapabilities] = useState({});
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get("/finance/integrations/capability");
      setCapabilities(resp.data?.capabilities || {});
    } catch (e) {
      // 403 or network — capability unavailable for all categories (safe default)
      setCapabilities({});
      setError(e?.response?.data?.detail || "Failed to load capability status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  /** isActive: returns true ONLY when is_active=true for this category */
  const isActive  = (category) => capabilities[category] === true;

  /** statusOf: returns 'ACTIVE' or 'NOT_CONFIGURED' based on capability map */
  const statusOf  = (category) =>
    capabilities[category] === true ? "ACTIVE" : "NOT_CONFIGURED";

  return {
    capabilities,
    loading,
    error,
    isActive,
    statusOf,
    refresh: fetchStatuses,
  };
}
